/**
 * YAML writer — converts HubAsCodePayload to hub.yaml + agents/<slug>.yaml + agents/<slug>.md files
 * Reverse of parser.ts
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import * as yaml from 'js-yaml';
import { ensureUniqueSlug } from '@wayai/core';
import { slugify, resolveHubYamlPath } from './utils.js';
import { writeResourceFileTree } from './resource-files.js';
import type { HubAsCodePayload, HubAsCodeAgent, HubAsCodeResource, HubAsCodeEval, HubAsCodeJourney } from './types.js';

export const YAML_DUMP_OPTIONS: yaml.DumpOptions = {
  lineWidth: -1,      // don't wrap lines
  noRefs: true,        // don't use YAML anchors
  quotingType: '"',
  forceQuotes: false,
  sortKeys: false,
};

// Shared between writeEvalYamlFiles (pull) and `wayai eval capture` so the on-disk shape stays identical.
export function buildEvalYamlObject(evalEntry: HubAsCodeEval, slug: string): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (evalEntry.id) out.id = evalEntry.id;
  if (evalEntry.name !== slug) out.name = evalEntry.name;
  out.agent = evalEntry.agent;
  if (evalEntry.agent_id) out.agent_id = evalEntry.agent_id;
  if (typeof evalEntry.runs === 'number' && evalEntry.runs !== 1) {
    out.runs = evalEntry.runs;
  }
  if (evalEntry.enabled === false) {
    out.enabled = false;
  }
  if (evalEntry.history && evalEntry.history.length > 0) {
    out.history = evalEntry.history;
  }
  // Eval-session seed fixture (gh #3090) — emit near the top so the seed dependency
  // is visible before the scenario body. Blank/absent → omitted.
  if (typeof evalEntry.fixture === 'string' && evalEntry.fixture.trim()) {
    out.fixture = evalEntry.fixture.trim();
  }
  if (typeof evalEntry.target_base === 'string' && evalEntry.target_base.trim()) {
    out.target_base = evalEntry.target_base.trim();
  }
  // Per-run variables (gh #3007) — emit above input/expected so the reader sees
  // the fixture table before the `{{var()}}` references that consume it.
  if (evalEntry.variables && Object.keys(evalEntry.variables).length > 0) {
    out.variables = evalEntry.variables;
  }
  out.input = evalEntry.input;
  out.expected = evalEntry.expected;
  if (evalEntry.evaluator_instructions) {
    out.evaluator_instructions = evalEntry.evaluator_instructions;
  }
  return out;
}

/**
 * Write a HubAsCodePayload to disk as hub.yaml + agents/<slug>.yaml + agents/<slug>.md files.
 *
 * - Creates `hub.yaml` with hub config (no agents section)
 * - Creates `agents/<slug>.yaml` for each agent (all config except instructions)
 * - Creates `agents/<slug>.md` for each agent with instructions
 * - Removes orphan .yaml and .md files in agents/ that don't match any current agent
 * - Deletes old `wayai.yaml` if present (migration)
 */
export function writeHubFolder(hubFolder: string, payload: HubAsCodePayload): void {
  // Ensure directories exist
  const agentsDir = path.join(hubFolder, 'agents');
  if (!fs.existsSync(hubFolder)) {
    fs.mkdirSync(hubFolder, { recursive: true });
  }
  if (!fs.existsSync(agentsDir)) {
    fs.mkdirSync(agentsDir, { recursive: true });
  }

  // Build YAML-safe payload (no agents section)
  const yamlPayload = buildYamlPayload(payload);
  const agentFiles = extractAgentFiles(payload);

  // Write hub.yaml
  const yamlContent = yaml.dump(yamlPayload, YAML_DUMP_OPTIONS);
  fs.writeFileSync(path.join(hubFolder, 'hub.yaml'), yamlContent, 'utf-8');

  // Init-only: 1-line Claude Code shim that imports the hub's AGENTS.md.
  // Skipped if the user has edited it. Other harnesses load AGENTS.md natively.
  const claudeShimPath = path.join(hubFolder, 'CLAUDE.md');
  if (!fs.existsSync(claudeShimPath)) {
    fs.writeFileSync(claudeShimPath, '@AGENTS.md\n', 'utf-8');
  }

  // Delete old wayai.yaml if it exists (migration)
  const oldYamlPath = path.join(hubFolder, 'wayai.yaml');
  if (fs.existsSync(oldYamlPath)) {
    fs.unlinkSync(oldYamlPath);
  }

  // Write agent YAML config files + instruction files
  // Track .yaml and .md slugs separately so agents without instructions get their .md cleaned up
  const yamlSlugs = writeAgentYamlFiles(agentsDir, payload.agents || []);
  const mdSlugs = new Set<string>();
  for (const { slug, content } of agentFiles) {
    mdSlugs.add(slug);
    fs.writeFileSync(path.join(agentsDir, `${slug}.md`), content, 'utf-8');
  }

  // Remove orphan .yaml and .md files
  const existingFiles = fs.readdirSync(agentsDir);
  for (const file of existingFiles) {
    if (file.endsWith('.yaml')) {
      const slug = file.slice(0, -5);
      if (!yamlSlugs.has(slug)) {
        fs.unlinkSync(path.join(agentsDir, file));
      }
    } else if (file.endsWith('.md')) {
      const slug = file.slice(0, -3);
      if (!mdSlugs.has(slug)) {
        fs.unlinkSync(path.join(agentsDir, file));
      }
    }
  }

  // Write resource files
  writeResourceFiles(hubFolder, payload.resources || []);

  // Write eval scenario files (always run — handles transition from "evals exist" to "no evals")
  writeEvalYamlFiles(hubFolder, payload.evals || []);

  // Write journey files (always run — handles transition from "journeys exist" to "no journeys")
  writeJourneyYamlFiles(hubFolder, payload.journeys || []);
}

/**
 * Set or clear the top-level `preview_label` in a hub's `hub.yaml` in place,
 * preserving every other field. Used by `wayai relabel` so a label change touches
 * only the echoed metadata — never the agents/resources or any local edits a full
 * `writeHubFolder` would overwrite. Pass null to remove the key.
 *
 * Reads + re-dumps just `hub.yaml` with the same `YAML_DUMP_OPTIONS` the writer
 * uses (and `sortKeys: false`), so a synced file round-trips to itself; adding a
 * label that was previously absent appends the key (re-normalized on next pull).
 */
export function setPreviewLabelInHubYaml(hubFolder: string, label: string | null): void {
  const yamlPath = resolveHubYamlPath(hubFolder);
  if (!yamlPath) return;
  const obj = (yaml.load(fs.readFileSync(yamlPath, 'utf-8')) ?? {}) as Record<string, unknown>;
  if (label) obj.preview_label = label;
  else delete obj.preview_label;
  fs.writeFileSync(yamlPath, yaml.dump(obj, YAML_DUMP_OPTIONS), 'utf-8');
}

/**
 * Build the YAML-safe version of the payload (hub config only, no agents).
 * Agents are written to separate files by writeAgentYamlFiles().
 */
function buildYamlPayload(payload: HubAsCodePayload): Record<string, unknown> {
  const result: Record<string, unknown> = {
    version: payload.version,
    hub_id: payload.hub_id,
    hub_environment: payload.hub_environment,
  };

  // Echo back preview_label for preview hubs: read-only, server-owned metadata
  // (set via the backend replicate-preview / hub-update PATCH, not authored here).
  // Drives the `--<label>` hub-folder suffix (getHubFolderSlug in workspace.ts).
  if (payload.preview_label) {
    result.preview_label = payload.preview_label;
  }

  result.hub = payload.hub;

  // Agents are NOT included in hub.yaml — they live in agents/<slug>.yaml

  if (payload.resources && payload.resources.length > 0) {
    // Strip files from resource entries — filesystem is source of truth
    result.resources = payload.resources.map((r) => {
      const { files, ...rest } = r;
      return rest;
    });
  }

  if (payload.states && payload.states.length > 0) {
    result.states = payload.states;
  }

  if (payload.teams && payload.teams.length > 0) {
    result.teams = payload.teams;
  }

  if (payload.connections && payload.connections.length > 0) {
    result.connections = payload.connections;
  }

  if (payload.outbound_contacts && payload.outbound_contacts.length > 0) {
    result.outbound_contacts = payload.outbound_contacts;
  }

  if (payload.outbound_lists && payload.outbound_lists.length > 0) {
    result.outbound_lists = payload.outbound_lists;
  }

  if (payload.outbound_schedules && payload.outbound_schedules.length > 0) {
    result.outbound_schedules = payload.outbound_schedules;
  }

  return result;
}

/**
 * Write per-agent YAML config files to agents/ directory.
 * Each agent gets agents/<slug>.yaml with all config except instructions.
 * Returns the set of slugs written (for orphan cleanup).
 */
function writeAgentYamlFiles(agentsDir: string, agents: HubAsCodeAgent[]): Set<string> {
  const slugs = new Set<string>();
  for (const agent of agents) {
    const slug = slugify(agent.name);
    slugs.add(slug);
    const entry: Record<string, unknown> = { ...agent };

    // Instructions go to .md file, not .yaml
    delete entry.instructions;

    const yamlContent = yaml.dump(entry, YAML_DUMP_OPTIONS);
    fs.writeFileSync(path.join(agentsDir, `${slug}.yaml`), yamlContent, 'utf-8');
  }
  return slugs;
}

/**
 * Extract agent instruction files from the payload.
 */
function extractAgentFiles(
  payload: HubAsCodePayload
): Array<{ slug: string; content: string }> {
  const files: Array<{ slug: string; content: string }> = [];

  for (const agent of payload.agents || []) {
    if (agent.instructions) {
      files.push({
        slug: slugify(agent.name),
        content: agent.instructions,
      });
    }
  }

  return files;
}

// Writes evals/<slug>.yaml (root) or evals/<set>/<slug>.yaml; removes orphan .yaml files and empty set folders.
function writeEvalYamlFiles(hubFolder: string, evals: HubAsCodeEval[]): void {
  const evalsDir = path.join(hubFolder, 'evals');

  // No evals: clean the directory if it exists, otherwise no-op.
  if (evals.length === 0) {
    if (fs.existsSync(evalsDir)) {
      cleanEvalOrphans(evalsDir, new Set<string>());
      // Remove the now-empty top-level dir for tidiness.
      try {
        if (fs.readdirSync(evalsDir).length === 0) fs.rmdirSync(evalsDir);
      } catch { /* keep dir if non-empty (e.g. user-added files) */ }
    }
    return;
  }

  if (!fs.existsSync(evalsDir)) {
    fs.mkdirSync(evalsDir, { recursive: true });
  }

  // Track relative paths written (e.g. "greeting.yaml", "set-a/foo.yaml") for orphan cleanup.
  const writtenRelPaths = new Set<string>();

  for (const evalEntry of evals) {
    const slug = slugify(evalEntry.name);
    const setName = evalEntry.path && evalEntry.path.trim().length > 0 ? evalEntry.path : null;
    const relPath = setName ? `${setName}/${slug}.yaml` : `${slug}.yaml`;
    const targetPath = path.join(evalsDir, relPath);

    // Defense-in-depth: the backend's `validateScenarioSetName` doesn't
    // restrict path separators, so refuse to write any path that escapes
    // evals/. Same pattern as `cleanOrphanFiles` for resource files.
    if (!targetPath.startsWith(evalsDir + path.sep)) {
      console.warn(`  Warning: skipping eval "${evalEntry.name}" (scenario set "${setName}" escapes evals/ — possible bad backend data)`);
      continue;
    }

    if (setName) {
      const setDir = path.join(evalsDir, setName);
      if (!fs.existsSync(setDir)) {
        fs.mkdirSync(setDir, { recursive: true });
      }
    }

    const yamlContent = yaml.dump(buildEvalYamlObject(evalEntry, slug), YAML_DUMP_OPTIONS);
    fs.writeFileSync(targetPath, yamlContent, 'utf-8');
    writtenRelPaths.add(relPath);
  }

  cleanEvalOrphans(evalsDir, writtenRelPaths);
}

// Only touches .yaml files (user-added READMEs/notes are preserved); empty set subfolders are pruned.
function cleanEvalOrphans(evalsDir: string, writtenRelPaths: Set<string>): void {
  const entries = fs.readdirSync(evalsDir, { withFileTypes: true });

  for (const entry of entries) {
    const fullPath = path.join(evalsDir, entry.name);

    if (entry.isFile()) {
      if (entry.name.endsWith('.yaml') && !writtenRelPaths.has(entry.name)) {
        fs.unlinkSync(fullPath);
      }
      continue;
    }

    if (entry.isDirectory()) {
      const setName = entry.name;
      const subEntries = fs.readdirSync(fullPath, { withFileTypes: true });
      for (const sub of subEntries) {
        if (sub.isFile() && sub.name.endsWith('.yaml')) {
          const relPath = `${setName}/${sub.name}`;
          if (!writtenRelPaths.has(relPath)) {
            fs.unlinkSync(path.join(fullPath, sub.name));
          }
        }
        // Nested directories aren't valid eval layout — leave them alone.
      }
      // Remove now-empty scenario-set folder.
      try {
        if (fs.readdirSync(fullPath).length === 0) fs.rmdirSync(fullPath);
      } catch { /* ignore — non-empty (e.g. user notes) */ }
    }
  }
}

// Shared between writeJourneyYamlFiles (pull) and `wayai journey capture` so the
// on-disk shape stays identical. Field order mirrors the journey YAML design:
// id, name (omitted when == slug), agent, agent_id, enabled, evaluator_instructions,
// variables, transcript, step_config.
export function buildJourneyYamlObject(journeyEntry: HubAsCodeJourney, slug: string): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (journeyEntry.id) out.id = journeyEntry.id;
  if (journeyEntry.name !== slug) out.name = journeyEntry.name;
  out.agent = journeyEntry.agent;
  if (journeyEntry.agent_id) out.agent_id = journeyEntry.agent_id;
  if (journeyEntry.enabled === false) {
    out.enabled = false;
  }
  if (journeyEntry.evaluator_instructions) {
    out.evaluator_instructions = journeyEntry.evaluator_instructions;
  }
  // Eval-session seed fixture (gh #3090) — the journey owns its set, so this is the
  // set-level seed dependency. Blank/absent → omitted.
  if (typeof journeyEntry.fixture === 'string' && journeyEntry.fixture.trim()) {
    out.fixture = journeyEntry.fixture.trim();
  }
  if (typeof journeyEntry.target_base === 'string' && journeyEntry.target_base.trim()) {
    out.target_base = journeyEntry.target_base.trim();
  }
  // Per-run variables (gh #3007) — emit above the transcript so the fixture table
  // reads before the `{{var()}}` references that consume it.
  if (journeyEntry.variables && Object.keys(journeyEntry.variables).length > 0) {
    out.variables = journeyEntry.variables;
  }
  if (journeyEntry.transcript && journeyEntry.transcript.length > 0) {
    out.transcript = journeyEntry.transcript;
  }
  if (journeyEntry.step_config && Object.keys(journeyEntry.step_config).length > 0) {
    out.step_config = journeyEntry.step_config;
  }
  return out;
}

// Writes journeys/<slug>.yaml (flat folder — no scenario-set subfolders);
// removes orphan .yaml files. Same orphan-cleanup discipline as evals.
function writeJourneyYamlFiles(hubFolder: string, journeys: HubAsCodeJourney[]): void {
  const journeysDir = path.join(hubFolder, 'journeys');

  // No journeys: clean the directory if it exists, otherwise no-op.
  if (journeys.length === 0) {
    if (fs.existsSync(journeysDir)) {
      cleanJourneyOrphans(journeysDir, new Set<string>());
      try {
        if (fs.readdirSync(journeysDir).length === 0) fs.rmdirSync(journeysDir);
      } catch { /* keep dir if non-empty (e.g. user-added files) */ }
    }
    return;
  }

  if (!fs.existsSync(journeysDir)) {
    fs.mkdirSync(journeysDir, { recursive: true });
  }

  const writtenFiles = new Set<string>();
  // Disambiguate distinct journey names that slugify to the same value (e.g.
  // "Cancel Order" and "cancel order!") — without this they'd overwrite the same
  // file on pull and silently lose one journey. ensureUniqueSlug appends _2/_3…;
  // when the slug is disambiguated it diverges from name, so buildJourneyYamlObject
  // emits the `name` field, keeping the round-trip lossless.
  const usedSlugs = new Set<string>();
  for (const journeyEntry of journeys) {
    const slug = ensureUniqueSlug(slugify(journeyEntry.name), usedSlugs);
    const fileName = `${slug}.yaml`;
    const yamlContent = yaml.dump(buildJourneyYamlObject(journeyEntry, slug), YAML_DUMP_OPTIONS);
    fs.writeFileSync(path.join(journeysDir, fileName), yamlContent, 'utf-8');
    writtenFiles.add(fileName);
  }

  cleanJourneyOrphans(journeysDir, writtenFiles);
}

// Only touches .yaml files (user-added READMEs/notes are preserved). Journeys are
// a flat folder, so there are no set subfolders to prune.
function cleanJourneyOrphans(journeysDir: string, writtenFiles: Set<string>): void {
  const entries = fs.readdirSync(journeysDir, { withFileTypes: true });
  for (const entry of entries) {
    if (entry.isFile() && entry.name.endsWith('.yaml') && !writtenFiles.has(entry.name)) {
      fs.unlinkSync(path.join(journeysDir, entry.name));
    }
  }
}

/**
 * Write resource files to disk and clean up orphan resource directories.
 */
function writeResourceFiles(hubFolder: string, resources: HubAsCodeResource[]): void {
  const resourcesDir = path.join(hubFolder, 'resources');

  // Track current resource slugs for orphan cleanup
  const currentSlugs = new Set<string>();

  for (const resource of resources) {
    const resSlug = slugify(resource.name);
    currentSlugs.add(resSlug);
    const resDir = path.join(resourcesDir, resSlug);
    writeResourceFileTree(resDir, resource.files || []);
  }

  // Remove orphan resource directories
  if (fs.existsSync(resourcesDir)) {
    const existingDirs = fs.readdirSync(resourcesDir, { withFileTypes: true });
    for (const entry of existingDirs) {
      if (entry.isDirectory() && !currentSlugs.has(entry.name)) {
        fs.rmSync(path.join(resourcesDir, entry.name), { recursive: true, force: true });
      }
    }
  }
}
